import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

function InviteAccept() {
  const { token } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingInvite, setCheckingInvite] = useState(true);
  const [inviteReady, setInviteReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkInviteStatus = async () => {
      if (authLoading) {
        return;
      }

      if (isAuthenticated) {
        navigate('/', { replace: true });
        return;
      }

      if (!token) {
        if (!cancelled) {
          setError(t('auth.inviteFailed'));
          setInviteReady(false);
          setCheckingInvite(false);
        }
        return;
      }

      try {
        const response = await api.get(`/api/auth/invite/${token}/status`);
        if (cancelled) {
          return;
        }

        if (response.data?.status === 'accepted') {
          navigate('/login', { replace: true });
          return;
        }

        setInviteReady(response.data?.status === 'pending');
        setCheckingInvite(false);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.response?.data?.error || t('auth.inviteFailed'));
          setInviteReady(false);
          setCheckingInvite(false);
        }
      }
    };

    checkInviteStatus();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, navigate, t, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!inviteReady) {
      return;
    }
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/api/auth/invite/accept', { token, password });
      setSuccess(t('auth.inviteAccepted', { email: response.data.email }));
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('auth.inviteFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('auth.inviteTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.inviteSubtitle')}
        </Typography>
        {checkingInvite ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('common.loading')}
          </Alert>
        ) : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label={t('auth.password')}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            disabled={checkingInvite || submitting || !inviteReady}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('auth.confirmPassword')}
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
            disabled={checkingInvite || submitting || !inviteReady}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{ mt: 3 }}
            disabled={checkingInvite || submitting || !inviteReady}
          >
            {t('auth.acceptInvite')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default InviteAccept;
